import React, {memo, ReactElement, useMemo} from "react";
import {Props} from "./SentMessagesDetails.types";
import {useMagicReducer} from "@witivio_teamspro/use-reducer";
import {initialState, reducer} from "./SentMessagesDetails.reducer";
import {CompareModule} from "modules/Compare.module";
import "./SentMessagesDetails.styles.scss";
import {AcceptIcon, Button, DownloadIcon, Flex, Loader, Menu, SendIcon, Text, Tooltip} from "@fluentui/react-northstar";
import translations from "../../../translations";
import {useSentMessageDetailsCache} from "../../../hooks/useSentMessageDetailsCache";
import {VirtualizedList} from "../../VirtualizedList/VirtualizedList";
import {PeopleCard} from "./PeopleCard/PeopleCard";
import {SentMessageSentDetailsItem} from "../../../Interfaces/SentMessageSentDetails";

export const SentMessagesDetails = memo((props: Props): ReactElement | null => {
    const {sentDetails, isLoading} = useSentMessageDetailsCache(props.message.id);
    const [state, dispatch] = useMagicReducer(reducer, initialState(props.message));

    const successCount = sentDetails?.success?.length ?? 0;
    const failureCount = sentDetails?.failure?.length ?? 0;
    const viewsCount = sentDetails?.views?.length ?? 0;

    const menuItems = useMemo(() => {
        return generateMenuItems(successCount, failureCount, viewsCount, isLoading);
    }, [successCount, failureCount, viewsCount, isLoading]);

    const successListItems = useMemo(() => {
        return renderListItems(sentDetails?.success);
    }, [sentDetails?.success]);

    const failureListItems = useMemo(() => {
        return renderListItems(sentDetails?.failure);
    }, [sentDetails?.failure]);

    const viewsListItems = useMemo(() => {
        return renderListItems(sentDetails?.views);
    }, [sentDetails?.views]);

    const totalItems = (sentDetails?.success?.length ?? 0) + (sentDetails?.failure?.length ?? 0);

    let listItems: ReactElement[] = [];
    switch (state.menuIndex) {
        case 0:
            listItems = successListItems;
            break;
        case 1:
            listItems = failureListItems;
            break;
        case 2:
            listItems = viewsListItems;
            break;
    }

    return (
        <Flex fill column>
            <Flex space={"between"}>
                <Menu
                    activeIndex={state.menuIndex}
                    items={menuItems} underlined primary
                    onActiveIndexChange={dispatch("onChangeMenuIndex")}
                    styles={{border: 0}}
                />
                <Flex gap={"gap.small"}>
                    <Tooltip
                        open={state.showSuccessTooltip}
                        pointing
                        content={
                            <Flex vAlign={"center"} styles={{gap: "8px"}}>
                                <AcceptIcon styles={{color: "green"}} size={"smaller"}/>
                                <Text content={translations.translate("MessageSent")}/>
                            </Flex>
                        }
                        trigger={
                            <Button
                                primary
                                icon={<SendIcon outline/>}
                                content={translations.translate("ResendToUnread")}
                                onClick={dispatch("resendToUnread", {
                                    messageId: props.message.id,
                                    onResendMessage: props.onResendMessage
                                })}
                                disabled={successCount === 0 || viewsCount >= successCount || state.isResending}
                                loading={state.isResending}
                            />
                        }
                    />
                    {totalItems > 0 &&
                        <Button
                            tinted
                            icon={<DownloadIcon outline/>}
                            content={translations.translate("ExportData")}
                            onClick={dispatch("exportData", props.message.id)}
                            disabled={state.isExporting}
                            loading={state.isExporting}
                        />
                    }
                </Flex>
            </Flex>
            <Flex fill styles={{height: "220px", paddingTop: "10px"}} vAlign={"center"} hAlign={"center"}>
                {isLoading ?
                    <Loader/>
                    :
                    !totalItems ?
                        <Text styles={{color: "darkgray"}} content={translations.translate("NoMessage")}/>
                        :
                        <VirtualizedList items={listItems}/>
                }
            </Flex>
        </Flex>
    )
}, CompareModule.areObjectsEqual);

///////////////////////////////////////////////////// PURE METHODS /////////////////////////////////////////////////////

const generateMenuItems = (success: number, failure: number, views: number, isLoading: boolean) => [
    {
        key: 'success',
        content: translations.translate("TooltipSuccess") + (isLoading ? "" : " (" + success + ")"),
    },
    {
        key: 'failure',
        content: translations.translate("TooltipFailure") + (isLoading ? "" : " (" + failure + ")"),
    },
    {
        key: 'views',
        content: translations.translate("TooltipViews") + (isLoading ? "" : " (" + views + ")"),
    }
];

const renderListItems = (items: SentMessageSentDetailsItem[] | undefined) => {
    return items?.sort((a, b) => a.userMail.localeCompare(b.userMail)).map(renderListItem) ?? [];
}

const renderListItem = (item: SentMessageSentDetailsItem) => {
    return (
        <Flex fill styles={{paddingBottom: "10px"}}>
            <PeopleCard item={item}/>
        </Flex>
    )
}